import { IAudition, ILocation } from '../../../../types/global/helper';
import { IAuditionUpdatePayload } from '../../../../redux/audition/types';

const EMPTY_LOCATION: ILocation = {
    type: 'Point',
    coordinates: [0, 0],
    address: '',
    description: '',
    short_name: '',
};

/** Normalize legacy array API values to a single point (parity with shoot_location). */
function seedLocationField(raw: unknown): ILocation {
    if (Array.isArray(raw)) {
        if (raw.length === 0) return { ...EMPTY_LOCATION };
        const first = raw[0];
        if (first && typeof first === 'object') return first as ILocation;
        return { ...EMPTY_LOCATION };
    }
    if (raw && typeof raw === 'object') {
        return raw as ILocation;
    }
    return { ...EMPTY_LOCATION };
}

/** Initial form state for the audition details edit modal (all sections). */
export function seedAllEditedValues(audition: IAudition): Record<string, any> {
    return {
        title: audition.title,
        synopsis: audition.synopsis || '',
        plot: audition.plot || '',
        company_website: audition.company_website || '',
        production_personel: audition.production_personel || '',
        description: audition.description || '',
        requirements: audition.requirements || [],
        required_media: audition.required_media || [],
        required_skill: audition.required_skill || [],
        min_age: audition.min_age || 1,
        max_age: audition.max_age || 100,
        compensation: audition.compensation || 'unpaid',
        employment_type: audition.employment_type || 'one-off',
        reward: audition.reward || 0,
        min_reward: audition.min_reward || 0,
        max_reward: audition.max_reward || 0,
        reward_frequency: audition.reward_frequency || 'gig',
        currency: audition.currency || 'USD',
        shoot_location: audition.shoot_location || {
            type: 'Point',
            coordinates: [0, 0],
            address: 'worldwide',
            description: 'remote',
        },
        work_location: seedLocationField(audition.work_location),
        talent_location: seedLocationField(audition.talent_location),
        job_start_date: audition.job_start_date || '',
        job_end_date: audition.job_end_date || '',
        rehearsal_date: audition.rehearsal_date || '',
        casting_expiration: audition.casting_expiration || '',
        attachment: audition.attachment || '',
        attachment_description: audition.attachment_description || '',
        attachment_type: audition.attachment_type || '',
        submission_report: audition.submission_report || 'daily digest',
        production_company: audition.production_company || null,
        tags: audition.tags || [],
        union_affiliation: audition.union_affiliation || '',
        type_of_work: audition.type_of_work || '',
        usage: audition.usage || [],
        gig_type: audition.gig_type || '',
    };
}

/** Single PATCH-style body merging every editable details field. */
export function buildFullUpdatePayload(v: Record<string, any>): IAuditionUpdatePayload {
    const body: IAuditionUpdatePayload = {
        title: v.title,
        synopsis: v.synopsis,
        plot: v.plot,
        company_website: v.company_website,
        production_personel: v.production_personel,
        description: v.description,
        requirements: v.requirements,
        required_media: v.required_media,
        required_skill: v.required_skill,
        min_age: v.min_age,
        max_age: v.max_age,
        compensation: v.compensation,
        employment_type: v.employment_type,
        reward: v.reward,
        min_reward: v.min_reward,
        max_reward: v.max_reward,
        reward_frequency: v.reward_frequency,
        currency: v.currency,
        shoot_location: v.shoot_location,
        work_location: v.work_location,
        talent_location: v.talent_location,
        job_start_date: v.job_start_date,
        job_end_date: v.job_end_date,
        rehearsal_date: v.rehearsal_date,
        casting_expiration: v.casting_expiration,
        attachment: v.attachment,
        attachment_description: v.attachment_description,
        attachment_type: v.attachment_type,
        submission_report: v.submission_report,
        production_company: v.production_company?._id ?? v.production_company ?? null,
        tags: v.tags,
        union_affiliation: v.union_affiliation,
        type_of_work: v.type_of_work,
        usage: v.usage,
    };
    if (v.timezone != null && v.timezone !== '') {
        body.timezone = v.timezone;
    }
    return body;
}
